import { useEffect } from "react";
import {
  StyleSheet,
  Text,
  View,
  Platform,
  Pressable,
  ScrollView,
} from "react-native";
import { useTranslation } from "react-i18next";
import { ms } from "@/ui/moderateScale";
import { useUISlice } from "@/models/useUISlice";
import { Colors } from "@/constants";
import Icon from "./Icon";
import { MenuAction } from "./Menu";
import { i18n } from "@/locales";
import Menu from "./Menu";
import { appLanguages } from "@/locales";
import { useAuth } from "@/models/useAuth";
import { useRouter } from "@/_router";
import { getPlatform } from "@/models/platform";
import { logFunnelEvent } from "@/fetchData/datadog";

const AlertOnboarding = () => {
  const onboardingVisible = useUISlice((state) => state.alertOnboardingVisible);
  const alertPromptSignIn = useUISlice((state) => state.alertPromptSignIn);
  const { t } = useTranslation();
  const width = useUISlice((state) => state.device.width);
  const isMobile = useUISlice((state) => state.device.isMobile);
  const router = useRouter();

  useEffect(() => {
    logFunnelEvent("[0] - page visited");

    // Disable scrolling when modal is visible
    document.body.style.overflow = "hidden";

    return () => {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (getPlatform() === "web" && isMobile) {
      // Apply the overlay color to the theme-color meta tag for web
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      if (metaThemeColor) {
        metaThemeColor.setAttribute("content", "#999999");
      }

      return () => {
        if (metaThemeColor) {
          metaThemeColor.setAttribute("content", "");
        }
      };
    }
  }, [alertPromptSignIn]);

  const LanguageSelector = () => {
    const getCurrentLanguageLabel = () => {
      return (
        appLanguages.find((lang) => lang.value === i18n.language)?.label ||
        "English"
      );
    };
    const languageActions: MenuAction[] = appLanguages.map((lang) => ({
      text: lang.label,
      onPress: () => {
        i18n.changeLanguage(lang.value);
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("lang", lang.value);
        window.location.search = urlParams.toString();
      },
    }));

    return (
      <Menu
        actionComponent={
          <View style={s.languageButton}>
            <Text style={s.languageButtonText}>
              {getCurrentLanguageLabel()}
            </Text>
            <Icon
              icon="caretRight"
              style={{
                transform: [{ rotate: "90deg" }],
                tintColor: Colors.text,
                width: ms(14),
                height: ms(14),
              }}
            />
          </View>
        }
        actions={languageActions}
        position="bottom right"
      />
    );
  };

  const _onCTAButtonPress = () => {
    useAuth().signIn({
      callback: () => {
        useUISlice.getState().setAlertOnboardingVisible(undefined);
        router.replace("/");
      },
    });
  };

  if (!onboardingVisible) return null;

  return (
    <View
      style={
        [
          s.container,
          Platform.OS === "web" && {
            position: "fixed" as any,
            width: "100vw" as any,
            height: "100vh" as any,
          },
          isMobile && { justifyContent: "flex-end" },
        ] as any
      }
    >
      <Pressable style={s.backdrop} />

      <View
        style={
          {
            borderRadius: ms(16),
            paddingHorizontal: ms(24),
            paddingTop: ms(20),
            paddingBottom: ms(20),
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            width: Math.min(width - ms(32), 800),
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            ...(isMobile && {
              width: width,
              borderBottomLeftRadius: ms(0),
              borderBottomRightRadius: ms(0),
              paddingHorizontal: ms(16),
              paddingBottom: ms(100),
            }),
          } as any
        }
      >
        <ScrollView style={{ flex: 1 }}>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginHorizontal: -ms(16),
              paddingHorizontal: ms(16),
              height: ms(60),
            }}
          >
            <Text style={s.logo}>⚡️ EZWORDING</Text>

            <LanguageSelector />
          </View>

          <View style={s.heroSection}>
            <Text
              accessibilityRole="header"
              aria-level={1}
              style={StyleSheet.flatten([
                s.title,
                isMobile && { fontSize: ms(28) },
              ])}
            >
              {t("alertOnboarding.title")}
            </Text>
            <Text
              style={{
                ...s.subtitle,
                ...(isMobile && { fontSize: ms(16) }),
              }}
            >
              {t("alertOnboarding.subtitle")}
            </Text>

            <Pressable
              onPress={_onCTAButtonPress}
              style={{
                backgroundColor: Colors.accent,
                height: ms(64),
                paddingHorizontal: ms(32),
                alignItems: "center",
                borderRadius: ms(60),
                marginTop: ms(10),
                flexDirection: "row",
                ...(isMobile && {
                  paddingHorizontal: ms(24),
                }),
              }}
            >
              <Text
                numberOfLines={1}
                style={{
                  marginRight: ms(16),
                  fontSize: ms(16),
                  fontWeight: "600",
                  color: Colors.white,
                }}
              >
                {t("alertOnboarding.button")}
              </Text>

              <Icon
                icon="caretRight"
                style={{
                  tintColor: Colors.white,
                  width: ms(24),
                  height: ms(24),
                }}
              />
            </Pressable>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

AlertOnboarding.OVERLAY_COLOR = "rgba(0, 0, 0, 0.4)";

const s = StyleSheet.create({
  container: {
    zIndex: 1000,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    position: "absolute",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    ...(Platform.OS === "ios" && {
      paddingTop: 0,
      marginTop: 0,
    }),
  },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: AlertOnboarding.OVERLAY_COLOR,
    backdropFilter: "blur(5px)",
  } as any,
  heroSection: {
    paddingTop: ms(80),
    paddingBottom: ms(60),
    paddingHorizontal: ms(24),
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    textAlign: "center",
    fontSize: ms(48),
    fontWeight: "700",
    color: "#1D1D1F",
    maxWidth: ms(800),
    width: "100%",
    alignSelf: "center",
    letterSpacing: -0.5,
  },
  subtitle: {
    marginVertical: ms(32),
    fontSize: ms(18),
    lineHeight: ms(28),
    color: Colors.text,
    textAlign: "center",
    maxWidth: ms(650),
    alignSelf: "center",
  },
  logo: {
    fontStyle: "italic",
    fontSize: ms(18),
    fontWeight: "900",
    letterSpacing: 0.8,
    color: Colors.text,
  },
  languageButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: ms(4),
  },
  languageButtonText: {
    fontSize: ms(16),
    color: Colors.text,
  },
  dropdownArrow: {
    fontSize: ms(14),
    color: Colors.text,
  },
}) as any;

export default AlertOnboarding;
